import {
  Content,
  HorizontalNav,
  Page,
} from '@trufla-technology/trumarket-react-design-system';
import { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { TruRoute } from '../../TruRoute';
import { UserDetailsSidebar } from './UserDetailsSidebar';
import { Teams } from '../Teams';
import { Locations } from '../Locations';

import './style.scss';
import {
  useGetTeams,
  useListAudits,
  useListLocations,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { Audits } from '../Audits';
import { useAuthContext } from '@trufla-technology/trufla-trudomain/dist/lib';

type Props = RouteComponentProps<{ key: string }>;

export const UserView = ({ match, location, history }: Props) => {
  const { isPermitted } = useAuthContext();
  const userKey = match.params.key;

  const {
    data: teams,
    get: getTeams,
    loading: teamsLoading,
    handleFilter: handleFilterTeams,
  } = useGetTeams(
    {
      paginate: {
        limit: 50,
        offset: 0,
      },
      filter: {
        userKeys: [userKey],
      },
    },
    { isInit: isPermitted('getTeams') },
  );

  const {
    data: audits,
    loading: auditsLoading,
    handleFilter: handleFilterAudits,
    handleOffset: handleOffsetAudits,
    query: auditsQuery,
    paging: auditsPaging,
  } = useListAudits(
    {
      paginate: {
        limit: 50,
        offset: 0,
      },
    },
    { isInit: isPermitted('listAudits') },
  );

  const {
    data: locations,
    get: getLocations,
    loading: locationsLoading,
    handleFilter: handleFilterLocation,
  } = useListLocations(
    {
      paginate: {
        limit: 50,
        offset: 0,
      },
      filter: {
        userKeys: [userKey],
      },
    },
    { isInit: isPermitted('listLocations') },
  );

  const tabs = [
    { label: 'Teams', path: 'teams', isPermitted: isPermitted('getTeams') },
    {
      label: 'Branches',
      path: 'branches',
      isPermitted: isPermitted('listLocations'),
    },
    { label: 'Audits', path: 'audits', isPermitted: isPermitted('listAudits') },
  ];

  const getDefaultTab = () => {
    const path = location.pathname.split('/').pop() as string;
    return tabs.some((tab) => tab.path === path) ? path : tabs[0].path;
  };

  const [activeTab, setActiveTab] = useState<string>(getDefaultTab());
  const handleTabClick = (path: string) => {
    history.push(`${match.url}/${path}`);
    setActiveTab(path);
  };

  useEffect(() => {
    const filter = { userKeys: [userKey] };
    handleFilterTeams(filter);
    handleFilterLocation(filter);
    handleFilterAudits(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  const TeamsRoute = useCallback(
    () => (
      <Teams teams={teams} refetchTeams={getTeams} loading={teamsLoading} />
    ),
    [teams, getTeams, teamsLoading],
  );

  const AuditsRoute = useCallback(
    () => (
      <Audits
        audits={audits}
        loading={auditsLoading}
        handleOffset={handleOffsetAudits}
        paging={auditsPaging}
        query={auditsQuery}
      />
    ),
    [audits, auditsLoading, auditsPaging, auditsQuery, handleOffsetAudits],
  );

  const LocationsRoute = useCallback(
    () => (
      <Locations
        locations={locations}
        refetchLocations={getLocations}
        loading={locationsLoading}
      />
    ),
    [locations, getLocations, locationsLoading],
  );

  return (
    <>
      <Content>
        <Page>
          <div className="flex-page">
            <UserDetailsSidebar
              key={userKey}
              teams={teams}
              locations={locations}
            />
            <div className="main-page tru-client-details client-tabs-wrapper">
              <HorizontalNav
                selected={false}
                active={activeTab}
                onClick={handleTabClick}
                tabs={tabs.filter((tab) => tab.isPermitted)}
              >
                <TruRoute path={`${match.path}`} component={TeamsRoute} />
                <TruRoute
                  path={`${match.path}/branches`}
                  component={LocationsRoute}
                />
                <TruRoute
                  path={`${match.path}/audits`}
                  component={AuditsRoute}
                />
              </HorizontalNav>
            </div>
          </div>
        </Page>
      </Content>
    </>
  );
};
